import React, { useState, useEffect, useMemo } from 'react';
import {
    StyleSheet,
    Text,
    View,
    TouchableOpacity,
    TextInput,
    ScrollView,
} from 'react-native';
import { createPortal } from 'react-dom';
import { useIsFocused } from '@react-navigation/native';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import DataTable from "react-data-table-component";
import { WorkState, useScale, ScaleFn , useDarkMode, TablePagination} from './common';
import Data from './Data';
import { getStorage } from '../storage';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
    bg:        '#0F172A',
    card:      '#1E293B',
    cardInner: '#0F172A',
    border:    'rgba(99,179,237,0.12)',
    text:      '#E2E8F0',
    textSub:   '#94A3B8',
    muted:     '#a8adb497',
    trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg:        '#F9FAFB',
    card:      '#FFFFFF',
    cardInner: '#F3F4F6',
    border:    '#E5E7EB',
    text:      '#111827',
    textSub:   '#4B5563',
    muted:     '#9CA3AF',
    trackBg:   'rgba(0,0,0,0.03)',
};
const S = {
    accent:  '#38BDF8',
    ok:      '#34D399',
    err:     '#F87171',
    warn:    '#FB923C',
    replace: '#FBBF24',
    purple:  '#A78BFA',
    pink:    '#F472B6',
};

// 스크롤 가능한 영역 안에서 발생한 이벤트인지 판별 (모달 내부 드롭다운 등)
const isInScrollable = (target: any) => {
    let n: any = target;
    while (n && n !== document.body && n.nodeType === 1) {
        const ov = getComputedStyle(n).overflowY;
        if ((ov === 'auto' || ov === 'scroll') && n.scrollHeight > n.clientHeight + 1) return true;
        n = n.parentElement;
    }
    return false;
};

const blockDrag = (el: any) => {
    if (!el || el._bd) return;
    el._bd = true;
    const pd = (e: Event) => { if (e.type === 'wheel' && isInScrollable(e.target)) return; e.preventDefault(); };
    el.addEventListener('mousedown', pd);
    el.addEventListener('touchstart', pd, { passive: false });
    el.addEventListener('wheel', pd, { passive: false });
    el.addEventListener('dragstart', pd);
};
const blockContentDrag = (el: any) => {
    if (!el || el._bcd) return;
    el._bcd = true;
    const pd = (e: Event) => { if (isInScrollable(e.target)) return; e.preventDefault(); e.stopPropagation(); };
    el.addEventListener('wheel', pd, { passive: false });
    el.addEventListener('touchmove', pd, { passive: false });
};

// ─── 테이블 컬럼 ──────────────────────────────────────
const managerColumns = [
    { id: 1, name: "no.", selector: (_row: any, index: any) => index + 1, grow: 0 },
    { id: 2, name: '이름', selector: (row: any) => row.NAME },
    { id: 3, name: '담당자 전화번호', selector: (row: any) => row.PHONE },
    { id: 4, name: '아이디', selector: (row: any) => row.ID },
    { id: 5, name: '이메일', selector: (row: any) => row.EMAIL },
    { id: 6, name: '근무 표시', selector: (row: any) => WorkState(row.WORKING) },
];

const memberColumns = [
    { id: 1, name: "no.", selector: (_row: any, index: any) => index + 1, grow: 0 },
    { id: 2, name: "이름", selector: (row: any) => row.NAME },
    { id: 3, name: '전화번호', selector: (row: any) => row.PHONE },
    { id: 4, name: '근무 표시', selector: (row: any) => WorkState(row.WORKING) },
];

const repeaterColumns = [
    { id: 1, name: "no.", selector: (_row: any, index: any) => index + 1, grow: 0 },
    { id: 2, name: '병실 호수', selector: (row: any) => row.ROOM_NO },
    { id: 3, name: '중계기 ID', selector: (row: any) => row.ID },
    { id: 4, name: "Ipv4 주소", selector: (row: any) => row.IPV4 },
];

// ─── 보호사 등록 모달 ─────────────────────────────────
const CaregiverModal = ({ visible, setVisible, area, getCaregiverList, T }: any) => {
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');

    useEffect(() => {
        if (visible) { setName(''); setPhone(''); }
    }, [visible]);

    const onRegister = () => {
        if (!name.trim()) { alert('이름을 입력하세요.'); return; }
        if (!phone.trim()) { alert('전화번호를 입력하세요.'); return; }
        Data.fetchData('user/initialRegistrationOfCaregiver', {
            NAME: name,
            PHONE: phone,
            AREA: area
        }, (result: any, count: any) => {
            if (count > 0) {
                setName('');
                setPhone('');
                setVisible(false);
                alert('등록되었습니다.');
                getCaregiverList();
            } else if (count === -2) {
                alert('해당 구역에 이미 등록된 전화번호입니다.');
            } else {
                alert('등록에 실패하였습니다.');
            }
        }, true);
    };

    if (!visible) return null;
    return createPortal(
        <View style={{ position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
            <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                <TouchableOpacity activeOpacity={1} onPress={() => setVisible(false)} style={{ flex: 1 }} />
            </View>
            <View ref={blockContentDrag} style={[styles.confirmModal, { backgroundColor: T.card, borderColor: T.border, width: s(400), alignItems: 'stretch', zIndex: 10 }]}>
                <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text, textAlign: 'center' }}>
                    보호사 등록
                </Text>
                <Text style={{ fontSize: s(13), color: T.textSub, textAlign: 'center', marginTop: s(6), marginBottom: s(24) }}>
                    보호사 정보를 입력하세요
                </Text>

                <View style={{ marginBottom: s(14) }}>
                    <Text style={{ fontSize: s(13), fontWeight: '600', color: T.textSub, marginBottom: s(6) }}>이름</Text>
                    <View style={[styles.modalInputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                        <TextInput
                            autoCapitalize='none'
                            clearButtonMode='always'
                            placeholder='이름을 입력하세요'
                            placeholderTextColor={T.muted}
                            style={{ fontSize: s(14), color: T.text, flex: 1, paddingHorizontal: s(14), height: '100%', outlineStyle: 'none' }}
                            onChangeText={setName}
                            value={name}
                        />
                    </View>
                </View>
                <View style={{ marginBottom: s(6) }}>
                    <Text style={{ fontSize: s(13), fontWeight: '600', color: T.textSub, marginBottom: s(6) }}>전화번호</Text>
                    <View style={[styles.modalInputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                        <TextInput
                            autoCapitalize='none'
                            clearButtonMode='always'
                            placeholder='전화번호를 입력하세요'
                            placeholderTextColor={T.muted}
                            keyboardType='numeric'
                            style={{ fontSize: s(14), color: T.text, flex: 1, paddingHorizontal: s(14), height: '100%', outlineStyle: 'none' }}
                            onChangeText={setPhone}
                            value={phone}
                        />
                    </View>
                </View>

                <View style={{ flexDirection: 'row', justifyContent: 'center', gap: s(10), marginTop: s(24) }}>
                    <TouchableOpacity
                        style={[styles.modalBtn, { borderWidth: 1, borderColor: T.border, backgroundColor: 'transparent' }]}
                        onPress={() => setVisible(false)}
                    >
                        <Text style={{ fontSize: s(14), fontWeight: '600', color: T.textSub }}>취소</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.modalBtn, { backgroundColor: S.accent }]}
                        onPress={onRegister}
                    >
                        <Text style={{ fontSize: s(14), fontWeight: '700', color: '#fff' }}>등록</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </View>,
        document.body
    );
};

// ─── 삭제 확인 모달 (공용) ────────────────────────────
const DeleteConfirmModal = ({ visible, setVisible, onConfirm, title, message, T }: any) => {
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    if (!visible) return null;
    return createPortal(
        <View style={{ position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
            <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                <TouchableOpacity activeOpacity={1} onPress={() => setVisible(false)} style={{ flex: 1 }} />
            </View>
            <View ref={blockContentDrag} style={[styles.confirmModal, { backgroundColor: T.card, borderColor: T.border, zIndex: 10 }]}>
                <Ionicons name="warning-outline" size={s(36)} color={S.err} style={{ marginBottom: s(12) }} />
                <Text style={{ fontSize: s(16), fontWeight: '700', color: T.text, textAlign: 'center' }}>{title}</Text>
                <Text style={{ fontSize: s(12), color: S.err, marginTop: s(8) }}>이 작업은 되돌릴 수 없습니다.</Text>
                <Text style={{ fontSize: s(13), color: T.textSub, textAlign: 'center', marginTop: s(4) }}>{message}</Text>
                <View style={{ flexDirection: 'row', justifyContent: 'center', gap: s(12), marginTop: s(20) }}>
                    <TouchableOpacity style={[styles.modalBtn, { backgroundColor: S.err }]} onPress={onConfirm}>
                        <Text style={styles.modalBtnText}>삭제</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={[styles.modalBtn, { backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }]}
                        onPress={() => setVisible(false)}>
                        <Text style={[styles.modalBtnText, { color: T.textSub }]}>취소</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </View>,
        document.body
    );
};

// ─── 중계기 등록 모달 ─────────────────────────────────
const AddRepeaterModal = ({ visible, setVisible, area, onSuccess, T }: any) => {
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const [form, setForm] = useState({ ROOM_NO: '', ID: '', IPV4: '' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (visible) setForm({ ROOM_NO: '', ID: '', IPV4: '' });
    }, [visible]);

    const set = (key: string, val: any) => setForm(prev => ({ ...prev, [key]: val }));

    const handleSubmit = () => {
        if (!form.ROOM_NO.trim()) { alert('병실 호수를 입력해주세요.'); return; }
        if (!form.ID.trim()) { alert('중계기 ID를 입력해주세요.'); return; }
        if (!form.IPV4.trim()) { alert('IPv4 주소를 입력해주세요.'); return; }
        setSubmitting(true);
        Data.fetchData('tabRepeater/insertNewRepeater', {
            ROOM_NO: form.ROOM_NO, ID: form.ID, IPV4: form.IPV4, AREA: area,
        }, (result: any, count: any) => {
            setSubmitting(false);
            if (count === 1) {
                alert('중계기가 등록되었습니다.');
                setVisible(false);
                onSuccess();
            } else if (count === -2) {
                alert('이미 등록된 병실 호수입니다.');
            } else if (count === -3) {
                alert('이미 등록된 중계기 ID입니다.');
            } else {
                alert('등록에 실패하였습니다.');
            }
        }, true);
    };

    if (!visible) return null;
    return createPortal(
        <View style={{ position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
            <View ref={blockDrag} style={{ position: 'absolute' as any, top: 0, left: 0, right: 0, bottom: 0 }}>
                <TouchableOpacity activeOpacity={1} onPress={() => setVisible(false)} style={{ flex: 1 }} />
            </View>
            <View ref={blockContentDrag} style={[styles.confirmModal, { backgroundColor: T.card, borderColor: T.border, width: s(400), alignItems: 'stretch', zIndex: 10 }]}>
                <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text, textAlign: 'center' }}>
                    중계기 등록
                </Text>
                <Text style={{ fontSize: s(13), color: T.textSub, textAlign: 'center', marginTop: s(6), marginBottom: s(20) }}>
                    구역: {area}
                </Text>
                <View style={{ marginBottom: s(14) }}>
                    <Text style={{ fontSize: s(13), fontWeight: '600', color: T.textSub, marginBottom: s(6) }}>병실 호수 *</Text>
                    <View style={[styles.modalInputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                        <TextInput autoCapitalize="none" placeholder="예: 101" placeholderTextColor={T.muted}
                            style={{ fontSize: s(14), color: T.text, flex: 1, paddingHorizontal: s(14), height: '100%', outlineStyle: 'none' }}
                            onChangeText={v => set('ROOM_NO', v)} value={form.ROOM_NO} />
                    </View>
                </View>
                <View style={{ marginBottom: s(14) }}>
                    <Text style={{ fontSize: s(13), fontWeight: '600', color: T.textSub, marginBottom: s(6) }}>중계기 ID *</Text>
                    <View style={[styles.modalInputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                        <TextInput autoCapitalize="none" placeholder="중계기 ID 입력" placeholderTextColor={T.muted}
                            style={{ fontSize: s(14), color: T.text, flex: 1, paddingHorizontal: s(14), height: '100%', outlineStyle: 'none' }}
                            onChangeText={v => set('ID', v)} value={form.ID} />
                    </View>
                </View>
                <View style={{ marginBottom: s(6) }}>
                    <Text style={{ fontSize: s(13), fontWeight: '600', color: T.textSub, marginBottom: s(6) }}>IPv4 주소 *</Text>
                    <View style={[styles.modalInputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                        <TextInput autoCapitalize="none" placeholder="예: 192.168.0.1" placeholderTextColor={T.muted}
                            style={{ fontSize: s(14), color: T.text, flex: 1, paddingHorizontal: s(14), height: '100%', outlineStyle: 'none' }}
                            onChangeText={v => set('IPV4', v)} value={form.IPV4} />
                    </View>
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'center', gap: s(10), marginTop: s(24) }}>
                    <TouchableOpacity style={[styles.modalBtn, { borderWidth: 1, borderColor: T.border, backgroundColor: 'transparent' }]}
                        onPress={() => setVisible(false)}>
                        <Text style={{ fontSize: s(14), fontWeight: '600', color: T.textSub }}>취소</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={[styles.modalBtn, { backgroundColor: S.accent, opacity: submitting ? 0.5 : 1 }]}
                        onPress={handleSubmit} disabled={submitting}>
                        <Text style={{ fontSize: s(14), fontWeight: '700', color: '#fff' }}>{submitting ? '등록 중...' : '등록'}</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </View>,
        document.body
    );
};

// ─── 메인 컴포넌트 ────────────────────────────────────
export default function GroupDetail({ navigation, route }: any) {
    const [__p] = useState(() => {
        if (typeof window === 'undefined') return route?.params || {};
        try {
            const area = route?.params?.area;
            const key = `_navParams_GroupDetail_${area ?? ''}`;
            const saved = window.localStorage.getItem(key);
            if (saved) return JSON.parse(saved);
        } catch {}
        return route?.params || {};
    });
    const isFocused = useIsFocused();
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const data = __p?.data;
    const [manager, setManager] = useState<any[]>([]);
    const [member, setMember] = useState<any[]>([]);
    const [repeater, setRepeater] = useState<any[]>([]);
    const [area, setArea] = useState(data?.AREA);
    const [focus, setFocus] = useState(false);
    const [pushLevel, setPushLevel] = useState(1);
    const { dark, setDark } = useDarkMode();
    const [admin, setAdmin] = useState('1');
    const [caregiverModal, setCaregiverModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [checkedCaregivers, setCheckedCaregivers] = useState<any[]>([]);
    const [caregiverDeleteModal, setCaregiverDeleteModal] = useState(false);
    const [addRepeaterModal, setAddRepeaterModal] = useState(false);
    const [activeTab, setActiveTab] = useState<any>('manager');   // 항목별 그룹화 탭

  const [mgrPage, setMgrPage] = useState(1);  const [mgrPer, setMgrPer] = useState(8);
  const [memPage, setMemPage] = useState(1);  const [memPer, setMemPer] = useState(8);
  const [repPage, setRepPage] = useState(1);  const [repPer, setRepPer] = useState(8);
  const _mgrCur = Math.min(Math.max(1, mgrPage), Math.max(1, Math.ceil(((manager)||[]).length / mgrPer)));
  const mgrData = ((manager)||[]).slice((_mgrCur-1)*mgrPer, _mgrCur*mgrPer);
  const _memCur = Math.min(Math.max(1, memPage), Math.max(1, Math.ceil(((member)||[]).length / memPer)));
  const memData = ((member)||[]).slice((_memCur-1)*memPer, _memCur*memPer);
  const _repCur = Math.min(Math.max(1, repPage), Math.max(1, Math.ceil(((repeater)||[]).length / repPer)));
  const repData = ((repeater)||[]).slice((_repCur-1)*repPer, _repCur*repPer);

    useEffect(() => {
        if (!isFocused) return;
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
        getStorage('AutoLogin').then((v: any) => { if (v) setAdmin(v.admin ?? '1'); });
    }, [isFocused]);

    const T = dark ? DARK : LIGHT;

    const tableStyles = {
        headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(40) } },
        headCells:  { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
        rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(44), '&:hover': { backgroundColor: T.cardInner } } },
        cells:      { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
        pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
            pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
        noData:     { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
    };

    useEffect(() => {
        navigation.setOptions({
            headerStyle: { backgroundColor: '#304156', elevation: 0, shadowOpacity: 0, borderBottomWidth: 0 },
            headerTintColor: '#fff',
            headerTitleStyle: { fontWeight: '700' },
        });
    }, []);

    useEffect(() => {
        const anyModal = caregiverModal || deleteModal || caregiverDeleteModal || addRepeaterModal;
        if (!anyModal) return;
        const prevent = (e: Event) => { if (isInScrollable(e.target)) return; e.preventDefault(); e.stopPropagation(); };
        document.addEventListener('wheel', prevent, { passive: false } as any);
        document.addEventListener('touchmove', prevent, { passive: false } as any);
        return () => {
            // restored by event listener cleanup
            document.removeEventListener('wheel', prevent);
            document.removeEventListener('touchmove', prevent);
        };
    }, [caregiverModal, deleteModal, caregiverDeleteModal, addRepeaterModal]);

    const getCaregiverList = () => {
        Data.fetchData('group/getCaregiverList', {
            AREA: data?.AREA
        }, (result: any, count: any) => {
            if (count > 0) { setMember(result); }
            else { setMember([]); }
        }, true);
    };

    useEffect(() => {
        Data.fetchData('group/getListOfManagerByGroup', {
            AREA: data?.AREA
        }, (result: any, count: any) => {
            if (count > 0) { setManager(result); }
            else { setManager([]); }
        }, true);
        getCaregiverList();
        Data.fetchData('repeater/getRepeatersList', {
            AREA: data?.AREA
        }, (result: any, count: any) => {
            if (count > 0) { setRepeater(result); }
            else { setRepeater([]); }
        }, true);
        Data.fetchData('group/getGroupInfoByArea', {
            AREA: data?.AREA
        }, (result: any, count: any) => {
            if (count === 1) { setPushLevel(result[0]?.PUSH_LEVEL); }
        }, true);
    }, []);

    const onSave = () => {
        if (!focus) { alert('수정 모드에서 저장해주세요.'); return; }
        Data.fetchData('group/updateGroupInfoByID', {
            AREA: area,
            PUSH_LEVEL: pushLevel,
            IDX: data?.GROUP_ID
        }, (result: any, count: any) => {
            if (count === 1) {
                setFocus(false);
                alert('수정되었습니다.');
            } else {
                alert('같은 구역이 존재합니다.');
            }
        }, true);
    };

    const onDeleteGroup = () => {
        Data.fetchData('group/deleteGroup', {
            DATA: [{ "AREA": data?.AREA }]
        }, (result: any, count: any) => {
            if (count > 0) {
                alert('삭제되었습니다.');
                navigation.goBack();
            } else {
                alert('삭제에 실패하였습니다.');
            }
        }, true);
    };

    const onDeleteCaregivers = () => {
        Data.fetchData('sensor/deleteCaregiver', {
            DATA: checkedCaregivers
        }, (result: any, count: any) => {
            if (count > 0) {
                setCaregiverDeleteModal(false);
                setCheckedCaregivers([]);
                getCaregiverList();
                alert('삭제되었습니다.');
            } else {
                alert('삭제에 실패하였습니다.');
            }
        }, true);
    };

    const onCaregiverCheck = (state: any) => {
        const items = state.selectedRows.map((row: any) => ({ ID: row.ID }));
        setCheckedCaregivers(items);
    };

    // 상태 요약: 보호사·중계기 모두 있으면 정상
    const healthy = (member?.length ?? 0) > 0 && (repeater?.length ?? 0) > 0;
    const statusColor = healthy ? S.ok : S.warn;
    const statusLabel = healthy ? '정상' : '점검 필요';
    const statusDesc = healthy ? '그룹이 정상적으로 운영 중입니다.' : '보호사 또는 중계기가 없습니다. 확인이 필요합니다.';
    const SUMMARY = [
        { key: 'manager',  icon: 'people',            label: '담당자', value: (manager?.length ?? 0) + '명', color: S.accent },
        { key: 'member',   icon: 'shield-checkmark',  label: '보호사', value: (member?.length ?? 0) + '명',  color: S.purple },
        { key: 'repeater', icon: 'radio',             label: '중계기', value: (repeater?.length ?? 0) + '개', color: S.ok },
    ];

    return (
        <View style={{ flex: 1, backgroundColor: T.bg }}>
            <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: s(40) }}>
                {/* ─── 헤더 카드: 그룹명 + 상태 + 수정/삭제 ─── */}
                <View style={[styles.sectionCard, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                    <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <View style={{ flex: 1 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                                {focus ? (
                                    <TextInput
                                        autoCapitalize='none'
                                        style={{ fontSize: s(22), fontWeight: '800', color: T.text, borderWidth: 1, borderColor: S.accent, borderRadius: s(8), paddingHorizontal: s(10), paddingVertical: s(4), outlineStyle: 'none' } as any}
                                        onChangeText={setArea}
                                        value={area}
                                    />
                                ) : (
                                    <Text style={{ fontSize: s(22), fontWeight: '800', color: T.text }}>{area}</Text>
                                )}
                                <View style={{ backgroundColor: statusColor + '22', paddingHorizontal: s(10), paddingVertical: s(3), borderRadius: s(20) }}>
                                    <Text style={{ fontSize: s(12), fontWeight: '700', color: statusColor }}>{statusLabel}</Text>
                                </View>
                            </View>
                            <Text style={{ fontSize: s(13), color: T.textSub, marginTop: s(6) }}>{statusDesc}</Text>
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(8) }}>
                            <TouchableOpacity
                                style={[styles.headerBtn, { borderWidth: 1, borderColor: T.border, backgroundColor: focus ? S.warn + '18' : 'transparent' }]}
                                onPress={() => { setFocus(!focus); if (focus) setArea(data?.AREA); }}
                            >
                                <Text style={{ fontSize: s(13), fontWeight: '600', color: focus ? S.warn : T.textSub }}>{focus ? '취소' : '수정'}</Text>
                            </TouchableOpacity>
                            {focus && (
                                <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.accent }]} onPress={onSave}>
                                    <Text style={{ fontSize: s(13), fontWeight: '700', color: '#fff' }}>저장</Text>
                                </TouchableOpacity>
                            )}
                            {admin === '0' && (
                                <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.err }]} onPress={() => setDeleteModal(true)}>
                                    <Text style={{ fontSize: s(13), fontWeight: '700', color: '#fff' }}>삭제</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>
                </View>

                {/* ─── 핵심 요약 카드 3개 (클릭 시 해당 탭) ─── */}
                <View style={{ flexDirection: 'row', gap: s(14), marginBottom: s(16) }}>
                    {SUMMARY.map((c) => {
                        const on = activeTab === c.key;
                        return (
                            <TouchableOpacity
                                key={c.key}
                                activeOpacity={0.85}
                                onPress={() => setActiveTab(c.key)}
                                style={[styles.sectionCard, { flex: 1, marginBottom: 0, flexDirection: 'row', alignItems: 'center', gap: s(14), backgroundColor: T.card, shadowColor: dark ? 'transparent' : '#d9dbec', borderColor: on ? c.color : T.border, ...(on ? { borderWidth: 1.5 } : {}) }]}
                            >
                                <View style={{ width: s(46), height: s(46), borderRadius: s(12), backgroundColor: c.color + '18', justifyContent: 'center', alignItems: 'center' }}>
                                    <Ionicons name={c.icon as any} size={s(22)} color={c.color} />
                                </View>
                                <View>
                                    <Text style={{ fontSize: s(13), color: T.textSub, fontWeight: '600' }}>{c.label}</Text>
                                    <Text style={{ fontSize: s(22), fontWeight: '800', color: T.text }}>{c.value}</Text>
                                </View>
                            </TouchableOpacity>
                        );
                    })}
                </View>

                {/* ─── 항목별 그룹화: 탭 + 테이블 ─── */}
                <View style={[styles.sectionCard, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: s(12) }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: T.cardInner, borderRadius: s(10), padding: s(3), gap: s(2) }}>
                            {SUMMARY.map((t) => {
                                const on = activeTab === t.key;
                                const cnt = t.key === 'manager' ? (manager?.length ?? 0) : t.key === 'member' ? (member?.length ?? 0) : (repeater?.length ?? 0);
                                return (
                                    <TouchableOpacity key={t.key} onPress={() => setActiveTab(t.key)}
                                        style={{ flexDirection: 'row', alignItems: 'center', gap: s(6), paddingHorizontal: s(14), height: s(32), borderRadius: s(8), backgroundColor: on ? S.accent : 'transparent' }}>
                                        <Text style={{ fontSize: s(14), fontWeight: '700', color: on ? '#fff' : T.textSub }}>{t.label}</Text>
                                        <View style={{ backgroundColor: on ? 'rgba(255,255,255,0.25)' : S.accent + '22', paddingHorizontal: s(6), borderRadius: s(8), minWidth: s(18), alignItems: 'center' }}>
                                            <Text style={{ fontSize: s(11), fontWeight: '700', color: on ? '#fff' : S.accent }}>{cnt}</Text>
                                        </View>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(8) }}>
                            {activeTab === 'member' && admin === '0' && checkedCaregivers.length > 0 && (
                                <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.err }]} onPress={() => setCaregiverDeleteModal(true)}>
                                    <AntDesign name="delete" size={s(12)} color="#fff" style={{ marginRight: s(4) }} />
                                    <Text style={{ fontSize: s(13), fontWeight: '700', color: '#fff' }}>삭제 ({checkedCaregivers.length})</Text>
                                </TouchableOpacity>
                            )}
                            {activeTab === 'member' && (
                                <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.accent }]} onPress={() => setCaregiverModal(true)}>
                                    <AntDesign name="plus" size={s(12)} color="#fff" style={{ marginRight: s(4) }} />
                                    <Text style={{ fontSize: s(13), fontWeight: '700', color: '#fff' }}>등록</Text>
                                </TouchableOpacity>
                            )}
                            {activeTab === 'repeater' && admin === '0' && (
                                <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.accent }]} onPress={() => setAddRepeaterModal(true)}>
                                    <AntDesign name="plus" size={s(12)} color="#fff" style={{ marginRight: s(4) }} />
                                    <Text style={{ fontSize: s(13), fontWeight: '700', color: '#fff' }}>등록</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>
                    {activeTab === 'manager' && (
                        <DataTable columns={managerColumns} data={mgrData} customStyles={tableStyles}
                            noDataComponent={<View style={{ padding: s(30), alignItems: 'center' }}><Text style={{ fontSize: s(14), color: T.muted }}>등록된 담당자가 없습니다.</Text></View>} onRowClicked={() => {}} />
                    )}
                    {activeTab === 'manager' && (manager || []).length > 0 && (
                        <TablePagination rowCount={(manager||[]).length} perPage={mgrPer} page={_mgrCur}
                            onChangePage={setMgrPage} onChangePerPage={(n)=>{ setMgrPer(n); setMgrPage(1); }}
                            T={T} s={s} accent={S.accent} options={[8,10,15,20]} />
                    )}
                    {activeTab === 'member' && (
                        <DataTable columns={memberColumns} data={memData} customStyles={tableStyles}
                            selectableRows={admin === '0'} onSelectedRowsChange={onCaregiverCheck}
                            noDataComponent={<View style={{ padding: s(30), alignItems: 'center' }}><Text style={{ fontSize: s(14), color: T.muted }}>등록된 보호사가 없습니다.</Text></View>} onRowClicked={() => {}} />
                    )}
                    {activeTab === 'member' && (member || []).length > 0 && (
                        <TablePagination rowCount={(member||[]).length} perPage={memPer} page={_memCur}
                            onChangePage={setMemPage} onChangePerPage={(n)=>{ setMemPer(n); setMemPage(1); }}
                            T={T} s={s} accent={S.accent} options={[8,10,15,20]} />
                    )}
                    {activeTab === 'repeater' && (
                        <DataTable columns={repeaterColumns} data={repData} customStyles={tableStyles}
                            noDataComponent={<View style={{ padding: s(30), alignItems: 'center' }}><Text style={{ fontSize: s(14), color: T.muted }}>등록된 중계기가 없습니다.</Text></View>} onRowClicked={() => {}} />
                    )}
                    {activeTab === 'repeater' && (repeater || []).length > 0 && (
                        <TablePagination rowCount={(repeater||[]).length} perPage={repPer} page={_repCur}
                            onChangePage={setRepPage} onChangePerPage={(n)=>{ setRepPer(n); setRepPage(1); }}
                            T={T} s={s} accent={S.accent} options={[8,10,15,20]} />
                    )}
                </View>
            </ScrollView>

            <CaregiverModal
                visible={caregiverModal}
                setVisible={setCaregiverModal}
                area={data?.AREA}
                getCaregiverList={getCaregiverList}
                T={T}
            />
            <DeleteConfirmModal
                visible={deleteModal}
                setVisible={setDeleteModal}
                onConfirm={onDeleteGroup}
                title="구역 삭제"
                message="해당 구역과 관련된 모든 정보가 삭제됩니다."
                T={T}
            />
            <DeleteConfirmModal
                visible={caregiverDeleteModal}
                setVisible={setCaregiverDeleteModal}
                onConfirm={onDeleteCaregivers}
                title="보호사 삭제"
                message={`선택한 ${checkedCaregivers.length}명의 보호사를 삭제합니다.`}
                T={T}
            />
            <AddRepeaterModal
                visible={addRepeaterModal}
                setVisible={setAddRepeaterModal}
                area={data?.AREA}
                onSuccess={() => {
                    Data.fetchData('repeater/getRepeatersList', {
                        AREA: data?.AREA
                    }, (result: any, count: any) => {
                        if (count > 0) { setRepeater(result); }
                        else { setRepeater([]); }
                    }, true);
                }}
                T={T}
            />
        </View>
    );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
    container: {
        flex: 1,
        padding: s(24),
    },
    sectionCard: {
        borderRadius: s(16),
        borderWidth: 0.5,
        overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: s(20),
        elevation: 3,
        padding: s(20),
        marginBottom: s(16),
    },
    headerBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: s(10),
        paddingHorizontal: s(14),
        height: s(34),
    },
    infoTable: {
        borderWidth: 1,
        borderRadius: s(10),
        overflow: 'hidden',
    },
    infoRow: {
        flexDirection: 'row',
        minHeight: s(44),
    },
    infoCell: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: s(10),
        paddingHorizontal: s(8),
    },
    confirmModal: {
        alignSelf: 'center', width: s(340), borderRadius: s(16), borderWidth: 1, padding: s(30),
        alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.15, shadowRadius: s(20), elevation: 10,
    },
    modalBtn: { paddingHorizontal: s(24), paddingVertical: s(10), borderRadius: s(10), alignItems: 'center', minWidth: s(90) },
    modalBtnText: { fontSize: s(14), fontWeight: '700', color: '#fff' },
    modalInputBox: {
        borderWidth: 1,
        borderRadius: s(10),
        height: s(42),
    },
});
